import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import { asyncValues } from '@jira/polaris__lib-ts-utils/src/async-value.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State, ProductSettingsResponse } from './ProductSettingsResponse.types.tsx';
import { getProductSettings, getProductSettingsForSharedViews } from './getProductSettings.tsx';

type Args = [] | [UUID: string, isSharedView: boolean];

export const loadProductSettings = (...args: Args): Action<State, Props, Promise<void>> => {
	return async ({ setState, getState }) => {
		const [UUID, isSharedView] = args;
		const productSettingsState = getState().productSettings;

		if (productSettingsState.status === 'loaded') {
			return;
		}

		try {
			let productSettingsResponse: ProductSettingsResponse;
			if (UUID && isSharedView) {
				productSettingsResponse = await getProductSettingsForSharedViews(UUID);
			} else if (!UUID && !isSharedView) {
				productSettingsResponse = await getProductSettings();
			} else {
				throw new Error('Invalid arguments: must provide either 2 arguments or none');
			}

			const { financialYear, ...restProductSettings } = productSettingsResponse;

			setState({
				productSettings: asyncValues.loaded({
					...restProductSettings,
					dates: financialYear,
				}),
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.fetchProductSettingsFailed', error));
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.product-settings.load',
				'Failed to load product settings',
				error instanceof Error ? error : new Error(String(error)),
			);

			setState({
				productSettings: {
					status: 'failed',
					error: error instanceof Error ? error : new Error(String(error)),
				},
			});

			throw error;
		}
	};
};
